import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EQUIVALENCIAS, RECETAS, GLOSARIO } from "@/content/guias";
import { PlayCircle, Trash2, Plus, MapPin } from "lucide-react";

// B1 — conteos en vivo: reutiliza los endpoints existentes (limit=1, se lee "total") en vez de
// crear un endpoint nuevo solo para esto (Prompt 19 pide agregar únicamente 2 endpoints).
const COUNT_FETCHERS = {
  logistica_usa: async () => {
    const [a, b] = await Promise.all([
      api.get("/loads", { params: { tipo_viaje: "nacional_us", limit: 1 } }),
      api.get("/loads", { params: { tipo_viaje: "transfronterizo", limit: 1 } }),
    ]);
    return a.data.total + b.data.total;
  },
  nacionales: async () => (await api.get("/loads", { params: { tipo_viaje: "nacional_mx", limit: 1 } })).data.total,
  cruces_impo: async () => (await api.get("/cruces", { params: { sentido: "impo", limit: 1 } })).data.total,
  cruces_expo: async () => (await api.get("/cruces", { params: { sentido: "expo", limit: 1 } })).data.total,
  cajas_movs: async () => (await api.get("/cajas/movimientos", { params: { limit: 1 } })).data.total,
  unidades: async () => (await api.get("/unidades", { params: { limit: 1 } })).data.total,
  referencias_cummins: async () => (await api.get("/referencias-cummins", { params: { limit: 1 } })).data.total,
  tarifario: async () => (await api.get("/rate-cards", { params: { limit: 1 } })).data.total,
};

function EquivalenciasTable({ navigate }) {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    Object.entries(COUNT_FETCHERS).forEach(([key, fn]) => {
      fn()
        .then((n) => setCounts((c) => ({ ...c, [key]: n })))
        .catch(() => setCounts((c) => ({ ...c, [key]: null })));
    });
  }, []);

  return (
    <Card data-testid="guia-equivalencias-card">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>En tu Excel (TRAFICO.xlsx)</TableHead>
                <TableHead>En la plataforma</TableHead>
                <TableHead className="text-right">Registros</TableHead>
                <TableHead className="text-right"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {EQUIVALENCIAS.map((row) => (
                <TableRow key={row.excel} data-testid={`guia-equiv-row-${row.excel}`}>
                  <TableCell className="text-sm font-semibold">{row.excel}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{row.plataforma}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {row.countKey ? (counts[row.countKey] ?? "…") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      data-testid={`guia-equiv-goto-${row.excel}`}
                      onClick={() => navigate(row.to)}
                    >
                      <MapPin className="h-3.5 w-3.5" /> {row.boton}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function RecetasList({ navigate }) {
  return (
    <Accordion type="single" collapsible className="w-full" data-testid="guia-recetas">
      {RECETAS.map((r, i) => (
        <AccordionItem key={r.titulo} value={`receta-${i}`} data-testid={`guia-receta-${i}`}>
          <AccordionTrigger className="text-sm font-semibold">{r.titulo}</AccordionTrigger>
          <AccordionContent>
            <ol className="ml-4 list-decimal space-y-1 text-sm text-muted-foreground">
              {r.pasos.map((p, j) => <li key={j}>{p}</li>)}
            </ol>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 gap-1"
              data-testid={`guia-receta-${i}-goto`}
              onClick={() => navigate(r.to)}
            >
              <MapPin className="h-3.5 w-3.5" /> Llévame ahí
            </Button>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function GlosarioList() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="guia-glosario">
      {GLOSARIO.map((g) => (
        <div key={g.termino} className="rounded-md border border-border bg-white p-3" data-testid={`guia-glosario-${g.termino}`}>
          <p className="text-sm font-semibold">{g.termino}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{g.definicion}</p>
        </div>
      ))}
    </div>
  );
}

function VideosSection() {
  const { user } = useAuth();
  const isDueno = user?.rol === "dueno";
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevoTitulo, setNuevoTitulo] = useState("");
  const [nuevaUrl, setNuevaUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setVideos(data.guia_videos || [])).finally(() => setLoading(false));
  }, []);

  const persist = async (nextVideos) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { guia_videos: nextVideos });
      setVideos(data.guia_videos || []);
      toast.success("Videos actualizados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addVideo = () => {
    if (!nuevoTitulo.trim() || !nuevaUrl.trim()) return;
    persist([...videos, { titulo: nuevoTitulo.trim(), url: nuevaUrl.trim() }]);
    setNuevoTitulo("");
    setNuevaUrl("");
  };

  const removeVideo = (idx) => persist(videos.filter((_, i) => i !== idx));

  if (loading) return null;

  return (
    <div data-testid="guia-videos-section">
      {videos.length === 0 && <p className="text-sm text-muted-foreground" data-testid="guia-videos-empty">Próximamente.</p>}
      <div className="space-y-2">
        {videos.map((v, i) => (
          <div key={i} className="flex items-center justify-between rounded-md border border-border bg-white p-3" data-testid={`guia-video-${i}`}>
            <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-medium text-primary hover:underline">
              <PlayCircle className="h-4 w-4" /> {v.titulo}
            </a>
            {isDueno && (
              <Button size="icon" variant="ghost" onClick={() => removeVideo(i)} disabled={saving} data-testid={`guia-video-${i}-remove`}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {isDueno && (
        <div className="mt-4 flex flex-col gap-2 rounded-md border border-dashed border-border p-3 sm:flex-row sm:items-end">
          <Input placeholder="Título del video" value={nuevoTitulo} onChange={(e) => setNuevoTitulo(e.target.value)} data-testid="guia-video-titulo-input" />
          <Input placeholder="URL de Loom o YouTube" value={nuevaUrl} onChange={(e) => setNuevaUrl(e.target.value)} data-testid="guia-video-url-input" />
          <Button onClick={addVideo} disabled={saving} className="gap-1 whitespace-nowrap" data-testid="guia-video-add-btn">
            <Plus className="h-4 w-4" /> Agregar
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Guias() {
  const navigate = useNavigate();

  return (
    <div data-testid="guias-page">
      <PageHeader title="Guías" subtitle="Dónde quedó cada hoja de tu Excel, recetas paso a paso, glosario y videos." />

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">¿Dónde quedó cada hoja de mi Excel?</h2>
        <EquivalenciasTable navigate={navigate} />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Cómo hacer las cosas de siempre</h2>
        <Card><CardContent className="p-4"><RecetasList navigate={navigate} /></CardContent></Card>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Glosario</h2>
        <GlosarioList />
      </section>

      <section>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Videos</h2>
        <VideosSection />
      </section>
    </div>
  );
}
